import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { Portal } from '@gorhom/portal'
import { Formik } from 'formik'
import Box, { BoxProps } from '@components/kit/box'
import Loading from '@components/kit/loading'
import Text from '@components/kit/text'
import FlatList from '@components/kit/flat-list'
import Form from '@components/kit/form'
import ScrollView from '@components/kit/scroll-view'
import { SelectFieldFormated, TextFieldFormated } from '@components/formFields'
import Pressable from '@components/kit/pressable'
import DateTimePicker, {
  ImperativeDateTimePickerProps
} from '@components/kit/datetime-picker'
import Button from '@components/kit/button'
import { useMedsStore } from '@store/Meds'
import { IAddMedication, ISearchedMed } from '@store/Meds/types'
import { ChatContext } from '../../chat.web'

enum AddMedState {
  Search = 'Search',
  Insert = 'Insert'
}

const HorizontalRule = ({
  marginY = '20',
  height = '2px',
  width = '100%',
  style = {},
  ...props
}: BoxProps) => (
  <Box
    width={width}
    height={height}
    marginY={marginY as any}
    style={{ backgroundColor: '#F9F9F9', ...(style as any) }}
    {...props}
  />
)

interface SearchMedicationsProps {
  onSelect: (medication: ISearchedMed) => void
}

function SearchMedications({ onSelect }: SearchMedicationsProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [lastTimeout, setLastTimeout] = useState<number | null>(null)
  const [medName, setMedName] = useState('')

  const [medications, setMedications] = useState<ISearchedMed[]>([])
  const [preventTyping, setPreventTyping] = useState(false)

  const { searchMedsByName } = useMedsStore.getState()

  const onEmpty = () => {
    setIsLoading(false)
    setMedName('')
    lastTimeout && clearTimeout(lastTimeout)
    setLastTimeout(null)
    setMedications([])
  }

  const onTyping = (content: string) => {
    if (preventTyping) {
      return
    }
    if (content === '') {
      onEmpty()
      return
    }
    setIsLoading(true)
    setMedName(content)
    if (lastTimeout) {
      clearTimeout(lastTimeout)
    }
    const typingTimeOut = window.setTimeout(() => {
      searchMeds(content)
    }, 2000)
    setLastTimeout(typingTimeOut)
  }

  const searchMeds = async (name: string) => {
    setIsLoading(true)

    let medsLength = 0

    return searchMedsByName(name)
      .then(meds => {
        setMedications(meds)
        setPreventTyping(false)
        medsLength = meds.length
      })
      .finally(() => {
        setIsLoading(false)
        // if (medsLength === 0) {
        //   console.log('No meds found')
        // }
      })
  }

  const renderSearchedMed = ({ item }: { item: ISearchedMed }): JSX.Element => {
    return (
      <Pressable onPress={() => onSelect(item)}>
        <Box
          padding='16'
          marginY='04'
          marginRight='08'
          marginLeft='04'
          // @ts-ignore
          style={{
            borderRadius: '12px',
            borderWidth: '1px',
            borderColor: '#F9F9F9',
            boxShadow: '0px 2px 4px 0px rgba(62, 64, 136, 0.08)'
          }}
        >
          <Text font='display.16.bold'>{item.drug_name}</Text>
        </Box>
      </Pressable>
    )
  }

  return (
    <Box
      overflowY='hidden'
      flexDirection='column'
      justifyContent='center'
      style={{ flexShrink: 1, height: '64vh' }}
    >
      <Text variant='h3' mb='24' textAlign='center'>
        Add a medication
      </Text>
      <Formik
        initialValues={{ medication: '' }}
        onSubmit={() => {}}
        validateOnChange={false}
      >
        {({ handleSubmit }) => (
          <Form>
            <TextFieldFormated
              name='medication'
              label='Medication Name'
              value={medName}
              onChangeText={text => onTyping(text)}
              placeholder='Enter task title'
              marginX='24'
            />
          </Form>
        )}
      </Formik>
      <HorizontalRule marginBottom='16' />
      {isLoading ? (
        <Box flex='1' justifyContent='center' alignItems='center'>
          <Loading />
        </Box>
      ) : (
        <Box style={{ flex: 1, overflow: 'hidden', paddingTop: '1em' }}>
          <ScrollView
            mb='20'
            style={{ flex: 1 }}
            contentContainerStyle={{ flexShrink: 1 }}
          >
            <FlatList
              pb='48'
              onEndReachedThreshold={0.5}
              onTouchStart={() => {
                setPreventTyping(true)
              }}
              data={medications}
              maxToRenderPerBatch={10}
              // @ts-ignore
              renderItem={({ item }: { item: ISearchedMed }) =>
                renderSearchedMed({ item })
              }
              keyExtractor={(item: ISearchedMed) => item.drug_name}
            />
          </ScrollView>
        </Box>
      )}
    </Box>
  )
}

const Row = ({ children }) => (
  <Box flexDirection='row' justifyContent='space-between' alignItems='center'>
    {children}
  </Box>
)

const LeftColumn = ({ children }) => (
  <Box justifyContent='flex-start' alignItems='center'>
    <Text font='display.14.bold'>{children}</Text>
  </Box>
)

const RightColumn = ({ children }) => (
  <Box justifyContent='flex-end' alignItems='center'>
    {children}
  </Box>
)

interface CustomNativeEventLayout {
  top: number
  left: number
  width: number
  height: number
}

interface InsertMedicationsProps {
  medication: ISearchedMed
  onCancel: () => void
  onSave: () => void
}

function InsertMedications({
  medication,
  onCancel,
  onSave
}: InsertMedicationsProps) {
  const { userId } = useContext(ChatContext)
  const { addMedication } = useMedsStore.getState()

  const [modalRootPosition, setModalRootPosition] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
  })
  const [startDatePickerPosition, setStartDatePickerPosition] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
  })

  const [endDatePickerPosition, setEndDatePickerPosition] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
  })

  const getCustomLayoutDimensions = (nativeEvent: any) => {
    const layout = nativeEvent.layout as any as CustomNativeEventLayout
    return {
      top: layout.top || 0,
      left: layout.left || 0,
      bottom: (layout.top || 0) + (layout.height || 0),
      right: (layout.left || 0) + (layout.width || 0)
    }
  }

  const startDatePickerRef = React.createRef<ImperativeDateTimePickerProps>()
  const endDatePickerRef = React.createRef<ImperativeDateTimePickerProps>()
  const formOptions = medication.dosage_forms.map(form => ({
    value: form,
    label: form.form
  }))
  const [selectedForm, setSelectedForm] = useState(formOptions?.[0])

  const [strengthOptions, setStrengthOptions] = useState(
    selectedForm?.value?.strengths?.map(strength => ({
      value: strength.gpi,
      label: strength.value
    })) || []
  )
  const [selectedStrength, setSelectedStrength] = useState(strengthOptions?.[0])

  const [startDate, setStartDate] = useState('')
  const [startReason, setStartReason] = useState('')
  const [endDate, setEndDate] = useState('')
  const [endReason, setEndReason] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [saveError, setSaveError] = useState('')

  const handleSelectForm = form => {
    setSelectedForm(form)
    setStrengthOptions(
      form?.value?.strengths?.map(strength => ({
        value: strength.gpi,
        label: strength.value
      })) || []
    )
    setSelectedStrength({
      value: form?.value?.strengths?.[0]?.gpi || '',
      label: form?.value?.strengths?.[0]?.value || ''
    })
  }

  const handleSelectStrength = strength => {
    setSelectedStrength(strength)
  }

  const openStartDatePicker = () => {
    startDatePickerRef?.current.present()
  }

  const openEndDatePicker = () => {
    endDatePickerRef?.current.present()
  }

  const handleSetStartDate = (date: moment.Moment) => {
    setStartDate(date.utc().format('MM/DD/YYYY'))
  }

  const handleSetEndDate = (date: moment.Moment) => {
    setEndDate(date.utc().format('MM/DD/YYYY'))
  }

  const validateForm = () => {
    return !!selectedStrength?.value && !!selectedForm.value.form && !!startDate
  }

  const handleSave = () => {
    if (isSubmitting || !validateForm()) {
      return
    }
    setIsSubmitting(true)
    const newMedication: IAddMedication = {
      gpi: selectedStrength?.value || selectedForm?.value?.strengths?.[0]?.gpi,
      start_date_of_service: moment(startDate, 'MM/DD/YYYY').format(
        'YYYY-MM-DD'
      ),
      start_reason: startReason,
      end_date_of_service: !!endDate
        ? moment(endDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
        : null,
      end_reason: !!endReason ? endReason : null,
      start_date_type: null,
      drug_name: medication.drug_name,
      drug_dosage_form: selectedForm.value.form,
      drug_strength: !!selectedStrength.label
        ? `${selectedStrength.label}`
        : null
    }
    addMedication(userId, newMedication)
      .then(result => {
        // on failure the modal stays open with everything still filled in, so
        // saving again is one click instead of retyping the whole entry
        if (!result?.success) {
          setSaveError(result?.errorMessage || 'Something went wrong')
          setIsSubmitting(false)
          return
        }

        onSave()
        setTimeout(() => {
          setIsSubmitting(false)
          onCancel()
        }, 1500)
      })
      .catch(() => {
        setSaveError('Something went wrong')
        setIsSubmitting(false)
      })
  }

  return (
    <Box
      overflowY='hidden'
      flexDirection='column'
      justifyContent='center'
      style={{ flexShrink: 1, maxHeight: '84vh' }}
    >
      <Box
        style={{ flex: 1, overflow: 'hidden' }}
        onLayout={({ nativeEvent }) =>
          setModalRootPosition(getCustomLayoutDimensions(nativeEvent))
        }
      >
        <Text variant='h3' mb='24' textAlign='center'>
          {medication.drug_name}
        </Text>

        <ScrollView overflowY='auto'>
          <Formik
            initialValues={{ medication: '' }}
            onSubmit={() => {}}
            validateOnChange={false}
          >
            {({ handleSubmit }) => (
              <Form>
                <Box flexDirection='column-reverse'>
                  <Row>
                    <LeftColumn>Reason</LeftColumn>
                    <RightColumn>
                      <TextFieldFormated
                        name='endReason'
                        label={null}
                        value={endReason}
                        valuePlaceholder='Write something...'
                        onChangeText={setEndReason}
                      />
                    </RightColumn>
                  </Row>
                  <HorizontalRule marginY='08' />
                  <Row>
                    <LeftColumn>End date</LeftColumn>
                    <RightColumn>
                      <Pressable onPress={openEndDatePicker}>
                        <TextFieldFormated
                          name='endDate'
                          label={null}
                          value={endDate}
                          valuePlaceholder='MM/DD/YYYY'
                          onChangeText={setEndDate}
                          onLayout={({ nativeEvent }) =>
                            setEndDatePickerPosition(
                              getCustomLayoutDimensions(nativeEvent)
                            )
                          }
                        />
                      </Pressable>
                      <Portal hostName='addMedHost'>
                        <DateTimePicker
                          position='absolute'
                          bottom={
                            modalRootPosition.bottom -
                            endDatePickerPosition.bottom
                          }
                          left={
                            endDatePickerPosition.right -
                            modalRootPosition.left +
                            20
                          }
                          onSubmit={handleSetEndDate}
                          ref={endDatePickerRef}
                          maxDate={moment()}
                          zIndex={99999}
                          hostName='addMedOverlayHost'
                        />
                      </Portal>
                    </RightColumn>
                  </Row>
                  <HorizontalRule marginY='08' />
                  <Row>
                    <LeftColumn>Reason</LeftColumn>
                    <RightColumn>
                      <TextFieldFormated
                        name='startReason'
                        label={null}
                        value={startReason}
                        valuePlaceholder='Write something...'
                        onChangeText={setStartReason}
                      />
                    </RightColumn>
                  </Row>
                  <HorizontalRule marginY='08' />
                  <Row>
                    <LeftColumn>Start date*</LeftColumn>
                    <RightColumn>
                      <Pressable onPress={openStartDatePicker}>
                        <TextFieldFormated
                          name='starDate'
                          label={null}
                          value={startDate}
                          valuePlaceholder='MM/DD/YYYY'
                          onChangeText={setStartDate}
                          onLayout={({ nativeEvent }) =>
                            setStartDatePickerPosition(
                              getCustomLayoutDimensions(nativeEvent)
                            )
                          }
                        />
                      </Pressable>
                      <Portal hostName='addMedHost'>
                        <DateTimePicker
                          position='absolute'
                          top={
                            startDatePickerPosition.top - modalRootPosition.top
                          }
                          left={
                            startDatePickerPosition.right -
                            modalRootPosition.left +
                            20
                          }
                          onSubmit={handleSetStartDate}
                          ref={startDatePickerRef}
                          maxDate={moment()}
                          zIndex={99999}
                          hostName='addMedOverlayHost'
                        />
                      </Portal>
                    </RightColumn>
                  </Row>
                  <HorizontalRule marginBottom='08' />
                  <Row>
                    <LeftColumn>Strength</LeftColumn>
                    <RightColumn>
                      <SelectFieldFormated
                        name='medStrength'
                        options={strengthOptions}
                        value={selectedStrength}
                        defaultValue={selectedStrength}
                        onChange={handleSelectStrength}
                      />
                    </RightColumn>
                  </Row>
                  <HorizontalRule />
                  <Row>
                    <LeftColumn>Form*</LeftColumn>
                    <RightColumn>
                      <SelectFieldFormated
                        name='medForm'
                        options={formOptions}
                        defaultValue={formOptions[0]}
                        onChange={handleSelectForm}
                      />
                    </RightColumn>
                  </Row>
                </Box>
              </Form>
            )}
          </Formik>
        </ScrollView>
        {!!saveError && (
          <Text
            font='text.12.medium'
            textColor='walrusRed800'
            textAlign='center'
            paddingTop='16'
          >
            {saveError}
          </Text>
        )}
        <Box
          flexDirection='row'
          justifyContent='center'
          alignItems='center'
          gap='12'
          paddingTop='24'
          paddingBottom='12'
        >
          <Button variant='tertiary' width='fit-content' onPress={onCancel}>
            Cancel
          </Button>
          <Button
            variant='violetGradient'
            width='fit-content'
            onPress={handleSave}
          >
            Save
          </Button>
        </Box>
      </Box>
      {isSubmitting && (
        <Portal hostName='addMedHost'>
          <Box
            position='absolute'
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg='white'
            opacity={0.5}
            justifyContent='center'
            alignItems='center'
          />
          <Loading />
        </Portal>
      )}
    </Box>
  )
}

interface AddMedicationsProps {
  onClose: () => void
  onSave: () => void
  hasClosed: boolean
}

function AddMedications({ onClose, onSave, hasClosed }: AddMedicationsProps) {
  const [currentStep, setCurrentStep] = useState<AddMedState>(
    AddMedState.Search
  )
  const [selectedMedication, setSelectedMedication] = useState(null)

  const handleSelectMedication = (medication: ISearchedMed) => {
    setSelectedMedication(medication)
    setCurrentStep(AddMedState.Insert)
  }

  useEffect(() => {
    if (hasClosed) {
      setCurrentStep(AddMedState.Search)
      setSelectedMedication(null)
    }
  }, [hasClosed])

  return (
    <Box>
      {currentStep === AddMedState.Search && (
        <SearchMedications onSelect={handleSelectMedication} />
      )}
      {currentStep === AddMedState.Insert && (
        <InsertMedications
          medication={selectedMedication}
          onCancel={() => {
            setCurrentStep(AddMedState.Search)
            onClose()
          }}
          onSave={onSave}
        />
      )}
    </Box>
  )
}

export default AddMedications
